/**
 * Lectura de la `evidence` de una alerta.
 *
 * **Toda alerta muestra la evidencia cruda que la disparó.** Es criterio de
 * aceptación de #42 y la razón de ser del módulo: un investigador de brand
 * protection que no puede ver por qué saltó una alerta deja de usar el
 * sistema en dos semanas.
 *
 * La evidencia es un JSONB que escribe el motor de anomalías
 * (`services/worker/rules/`) y su forma depende de la regla. El registry de
 * reglas puede crecer sin tocar la API ni el dashboard, así que acá **no se
 * traduce el nombre de los campos**: se muestran tal como los escribe el
 * worker y tal como aparecen en `GET /api/v1/alerts`. Un campo renombrado en
 * la pantalla y otro en el log es lo que hace que una investigación termine
 * comparando dos cosas distintas.
 *
 * Lo que sí se hace acá es tipar el JSON —viene como `unknown` y `any` está
 * prohibido— y esconder los identificadores internos.
 */

/** Ubicación de un escaneo, a nivel ciudad. Ver `RuleContext.location()`. */
export interface GeoPoint {
  at: string | null;
  country: string | null;
  city: string | null;
  lat: number | null;
  lon: number | null;
}

export type EvidenceValue =
  | { kind: 'text'; text: string }
  | { kind: 'number'; value: number }
  | { kind: 'bool'; value: boolean }
  | { kind: 'timestamp'; iso: string }
  | { kind: 'list'; items: EvidenceValue[] }
  | { kind: 'geo'; point: GeoPoint }
  | { kind: 'nested'; entries: EvidenceEntry[] }
  /** Campo con ids internos: existe, se dice que existe, no se pinta. */
  | { kind: 'hidden' }
  | { kind: 'empty' };

export interface EvidenceEntry {
  key: string;
  value: EvidenceValue;
}

/**
 * Campos que llevan `unit_id` interno, en claro o como rango de serie.
 * **Nunca se renderiza el `unit_id`**: es un id interno cuya ofuscación es
 * media razón de ser del formato del código (regla 3), y publicarlo en una
 * tabla del dashboard lo filtraría igual. Se muestra que el campo está, no su
 * contenido.
 */
const INTERNAL_ID_KEYS = new Set(['unit_id', 'serial_range', 'serial_from', 'serial_to']);

/**
 * Contexto del disparo que agrega `RuleContext.alert()` a TODAS las alertas.
 * Va al final: es el mismo en todas y no es lo que distingue a ésta.
 */
const TRIGGER_KEYS = new Set(['scanned_at', 'scan_type', 'result', 'country', 'city']);

/** La explicación en castellano que escribe la regla. Se muestra aparte. */
const WHY_KEY = 'why';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * `{at, country, city, lat, lon}` → punto. `null` si no tiene la forma: una
 * regla nueva puede meter un objeto anidado que no es una ubicación.
 */
export function readGeoPoint(value: unknown): GeoPoint | null {
  if (!isRecord(value)) return null;
  const hasGeoShape =
    'lat' in value || 'lon' in value || 'country' in value || 'city' in value;
  if (!hasGeoShape) return null;
  return {
    at: readString(value['at']),
    country: readString(value['country']),
    city: readString(value['city']),
    lat: readNumber(value['lat']),
    lon: readNumber(value['lon']),
  };
}

export function classifyValue(key: string, raw: unknown): EvidenceValue {
  if (INTERNAL_ID_KEYS.has(key)) return { kind: 'hidden' };
  if (raw === null || raw === undefined) return { kind: 'empty' };
  if (typeof raw === 'boolean') return { kind: 'bool', value: raw };
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? { kind: 'number', value: raw } : { kind: 'empty' };
  }
  if (typeof raw === 'string') {
    if (raw.length === 0) return { kind: 'empty' };
    return ISO_DATE.test(raw) ? { kind: 'timestamp', iso: raw } : { kind: 'text', text: raw };
  }
  if (Array.isArray(raw)) {
    return { kind: 'list', items: raw.map((item) => classifyValue(key, item)) };
  }
  const point = readGeoPoint(raw);
  if (point !== null) return { kind: 'geo', point };
  if (isRecord(raw)) {
    return {
      kind: 'nested',
      entries: Object.entries(raw).map(([nested, value]) => ({
        key: nested,
        value: classifyValue(nested, value),
      })),
    };
  }
  return { kind: 'empty' };
}

/** La frase que explica la regla, si la regla la escribió. */
export function evidenceWhy(evidence: Record<string, unknown>): string | null {
  return readString(evidence[WHY_KEY]);
}

/**
 * Toda la evidencia, ordenada para leerla: primero lo propio de la regla,
 * después el contexto del escaneo que llevan todas. `why` no viene acá porque
 * se muestra como titular.
 */
export function evidenceEntries(evidence: Record<string, unknown>): EvidenceEntry[] {
  const own: EvidenceEntry[] = [];
  const trigger: EvidenceEntry[] = [];
  for (const [key, raw] of Object.entries(evidence)) {
    if (key === WHY_KEY) continue;
    const entry: EvidenceEntry = { key, value: classifyValue(key, raw) };
    (TRIGGER_KEYS.has(key) ? trigger : own).push(entry);
  }
  return [...own, ...trigger];
}

// ────────────────────────────────────────────── viaje imposible y clonación

/** Los dos escaneos que no pueden ser la misma etiqueta. */
export interface TwoScans {
  from: GeoPoint;
  to: GeoPoint;
  /** Km entre los dos escaneos, si la regla lo calculó. */
  distanceKm: number | null;
  elapsedMinutes: number | null;
  speedKmh: number | null;
  maxSpeedKmh: number | null;
}

/**
 * Extrae los dos escaneos de un `impossible_travel` (`from`/`to`) o de un
 * `secret_reuse` (`first_use`/`second_use`). Son las dos reglas cuya evidencia
 * *es* un par de puntos en el mapa; el resto se lee como lista de campos.
 */
export function readTwoScans(evidence: Record<string, unknown>): TwoScans | null {
  const from = readGeoPoint(evidence['from']) ?? readGeoPoint(evidence['first_use']);
  const to = readGeoPoint(evidence['to']) ?? readGeoPoint(evidence['second_use']);
  if (from === null || to === null) return null;

  const elapsedSeconds = readNumber(evidence['elapsed_seconds']);
  const elapsedMinutes =
    readNumber(evidence['elapsed_minutes']) ??
    (elapsedSeconds === null ? null : elapsedSeconds / 60);

  return {
    from,
    to,
    distanceKm: readNumber(evidence['distance_km']),
    elapsedMinutes,
    speedKmh: readNumber(evidence['speed_kmh']),
    maxSpeedKmh: readNumber(evidence['max_speed_kmh']),
  };
}

// ──────────────────────────────────────────────────── resumen del renglón

/**
 * Los campos que deciden el triage de cada regla, para mostrarlos en el
 * renglón sin abrir el caso. La bandeja se recorre de arriba abajo muchas
 * veces por día: si para saber si algo importa hay que abrir cada fila, la
 * bandeja no sirve.
 */
const HIGHLIGHTS: Record<string, readonly string[]> = {
  not_activated: ['status_name', 'scan_count'],
  stolen_roll: ['roll_id', 'roll_status', 'unit_status'],
  recalled: ['lot_code', 'batch_status', 'trigger'],
  secret_reuse: ['elapsed_seconds', 'distinct_country'],
  impossible_travel: ['distance_km', 'elapsed_minutes', 'speed_kmh'],
  territory_diversion: ['territory', 'scanned_country', 'scanned_city'],
  geo_cluster: ['units_out_of_territory', 'threshold', 'countries_seen'],
  duplicate_scan: ['scan_count', 'threshold'],
  velocity_spike: ['sigma', 'rate_per_min', 'baseline_mean_per_min'],
  low_device_diversity: ['distinct_devices', 'scans', 'ratio'],
  datacenter_asn: ['asn', 'asn_owner'],
};

/** Hasta tres campos del renglón. Vacío nunca: se cae a los primeros que haya. */
export function highlights(
  alertType: string,
  evidence: Record<string, unknown>,
): EvidenceEntry[] {
  const preferred = HIGHLIGHTS[alertType] ?? [];
  const picked: EvidenceEntry[] = [];

  for (const key of preferred) {
    if (!(key in evidence)) continue;
    const value = classifyValue(key, evidence[key]);
    if (value.kind === 'empty' || value.kind === 'hidden') continue;
    picked.push({ key, value });
  }
  if (picked.length > 0) return picked.slice(0, 3);

  // Regla que este dashboard no conoce: se muestran los primeros campos
  // propios, que es más útil que no mostrar nada.
  return evidenceEntries(evidence)
    .filter((entry) => entry.value.kind !== 'empty' && entry.value.kind !== 'hidden')
    .slice(0, 3);
}
